/**
 * `/v1/contacts` and `/v1/contacts/:id/invite` — six of the eleven endpoints the Contacts page
 * consumes (`apps/api/src/contacts/contacts.controller.ts`).
 *
 * Every request is parsed with the schema the API serialised it with, and nothing here declares
 * a DTO of its own: `@tvitvi/shared` is the contract and this module only knows the paths.
 *
 * DELETE IS UNDOABLE, WHICH IS WHY THERE IS NO DIALOG (DESIGN.md §11.4). `DELETE /v1/contacts/:id`
 * is a **soft** delete that leaves the row untouched, and `POST /v1/contacts/:id/restore` brings
 * that very row back (QA5 A — it used to go by address and could wake a different contact).
 */
import {
  MAX_CONTACT_SUGGESTIONS,
  contactDtoSchema,
  contactListResponseSchema,
  contactSuggestionsResponseSchema,
  deleteContactResponseSchema,
  invitationCreatedResponseSchema,
  type ContactDto,
  type ContactListResponse,
  type ContactSuggestionField,
  type ContactSuggestionsResponse,
  type ContactSummaryDto,
  type CreateContactRequest,
  type CreateLinkedContactRequest,
  type DeleteContactResponse,
  type InvitationCreatedResponse,
  type InviteContactRequest,
  type UpdateContactRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk, type ContactListFilters } from '@/api/query-keys';

/** The list page size. One screenful of a dense 36px row table, several times over. */
const PAGE_SIZE = 100;

/** `?tagIds=a,b` — the comma form `uuidListQuerySchema` accepts, so one parameter, not five. */
function tagParam(tagIds: readonly string[] | undefined): string | undefined {
  return tagIds && tagIds.length > 0 ? tagIds.join(',') : undefined;
}

/**
 * The register, ordered by the server.
 *
 * SORTING IS THE SERVER'S JOB, and not because it is tidier. This endpoint answers one page of a
 * list that keyset-paginates over an index; sorting the page in the browser would order the
 * hundred rows that happened to arrive and leave the rest of the register in the old order behind
 * them — a list that is wrong about everything it is not currently showing.
 *
 * NO CURSOR TRAVELS WITH A SORT CHANGE. A cursor records the sort spec it was minted for and
 * `decodeCursor` refuses it under any other, so a stale one is a 400 rather than a wrong page.
 * There is nothing to drop here: the sort is part of `queryKey`, so a different order is a
 * different query with its own first page, and `filters` carries no position at all.
 */
export function contactsQueryOptions(orgId: string, filters: ContactListFilters) {
  return queryOptions({
    queryKey: qk.contactList(orgId, filters),
    queryFn: ({ signal }): Promise<ContactListResponse> =>
      apiRequest({
        path: '/contacts',
        query: {
          sort: filters.sort ?? 'name',
          direction: filters.direction ?? 'asc',
          limit: PAGE_SIZE,
          search: filters.search,
          tagIds: tagParam(filters.tagIds),
          companyId: filters.companyId,
          everyone: filters.everyone === true ? 'true' : undefined,
        },
        schema: contactListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * What this organization already calls a job title and a company.
 *
 * Atle: *"slik at man ikke skriver selskapsnavnet forskjellig med en feil"* — the endpoint exists
 * so the second person to type "Live Nation Norway" agrees with the first. It is a **suggestion
 * list, not a controlled vocabulary**: `createContactRequest` still accepts any short text, and
 * the first contact at a new company must be able to name it.
 *
 * REFERENCE TIER, not list tier. An organization's vocabulary changes a few times a week, and a
 * dropdown that refetches the same twenty words on every reopen is a round trip in front of a
 * keystroke. Absent prefix means "the most common openings", which is what the field shows the
 * moment it is focused.
 */
export function contactSuggestionsQueryOptions(
  orgId: string,
  field: ContactSuggestionField,
  prefix: string,
) {
  return queryOptions({
    queryKey: qk.contactSuggestions(orgId, field, prefix),
    queryFn: ({ signal }): Promise<ContactSuggestionsResponse> =>
      apiRequest({
        path: '/contacts/suggestions',
        query: {
          field,
          limit: MAX_CONTACT_SUGGESTIONS,
          ...(prefix.length > 0 ? { prefix } : {}),
        },
        schema: contactSuggestionsResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export function contactQueryOptions(orgId: string, contactId: string) {
  return queryOptions({
    queryKey: qk.contact(orgId, contactId),
    queryFn: ({ signal }): Promise<ContactDto> =>
      apiRequest({ path: `/contacts/${contactId}`, schema: contactDtoSchema, signal }),
    ...CACHE.detail,
  });
}

export function useCreateContact(
  orgId: string,
): UseMutationResult<ContactDto, unknown, CreateContactRequest> {
  return useMutation({
    mutationFn: (input: CreateContactRequest) =>
      apiRequest({ path: '/contacts', method: 'POST', body: input, schema: contactDtoSchema }),
    meta: { invalidates: [qk.contacts(orgId)] },
  });
}

/**
 * «Koble til en Tvitvi-bruker» (QA12 #8). Svaret er en vanlig kontakt med `link.status = pending`
 * — det samme enten adressen finnes eller ikke (8b), så klienten har ingenting å gjette ut fra.
 */
export function useCreateLinkedContact(
  orgId: string,
): UseMutationResult<ContactDto, unknown, CreateLinkedContactRequest> {
  return useMutation({
    mutationFn: (input: CreateLinkedContactRequest) =>
      apiRequest({
        path: '/contacts/linked',
        method: 'POST',
        body: input,
        schema: contactDtoSchema,
      }),
    meta: { invalidates: [qk.contacts(orgId)] },
  });
}

export interface UpdateContactInput {
  contactId: string;
  body: UpdateContactRequest;
}

export function useUpdateContact(
  orgId: string,
): UseMutationResult<ContactDto, unknown, UpdateContactInput> {
  return useMutation({
    mutationFn: ({ contactId, body }: UpdateContactInput) =>
      apiRequest({
        path: `/contacts/${contactId}`,
        method: 'PATCH',
        body,
        schema: contactDtoSchema,
      }),
    /*
     * FRIVILLIGE OGSÅ (QA13 runde 2, K-14).
     *
     * Ikke bare fordi «Løft til kontakt» skriver hit: Frivillige-lista henter navn, e-post og
     * telefon fra `contacts` med en `left join`, så ENHVER endring her endrer det den lista viser.
     * At nøkkelen manglet var en gammel feil som ikke var synlig før løftet ga den en knapp.
     *
     * Akkreditering og Personer henter navnet fra kontakten på samme måte, og hurtigredigering
     * skriver det herfra (29.09) — uten de to nøklene sto det gamle navnet til neste henting.
     */
    meta: {
      invalidates: [
        qk.contacts(orgId),
        qk.volunteers(orgId),
        qk.accreditations(orgId),
        qk.participations(orgId),
      ],
    },
  });
}

/**
 * Delete, optimistically, and hand back everything needed to put it back.
 *
 * The optimistic half is safe because it is reversible in both directions: the row leaves every
 * cached list immediately, a failure puts it back from the snapshot, and a success leaves the
 * user looking at a toast that can undo it for real.
 */
export interface DeletedContact {
  contact: ContactDto;
  /** Sak 207: `removed_from_register` angres med `PATCH inRegister: true`, ikke med restore. */
  outcome: DeleteContactResponse['outcome'];
}

export function useDeleteContact(
  orgId: string,
): UseMutationResult<DeletedContact, unknown, string, { lists: [readonly unknown[], unknown][] }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactId: string) => {
      // Read before removing: the note and the tags exist nowhere else once the row is gone.
      const contact = await apiRequest({
        path: `/contacts/${contactId}`,
        schema: contactDtoSchema,
      });
      const { outcome } = await apiRequest({
        path: `/contacts/${contactId}`,
        method: 'DELETE',
        schema: deleteContactResponseSchema,
      });
      return { contact, outcome };
    },
    onMutate: async (contactId) => {
      await queryClient.cancelQueries({ queryKey: qk.contacts(orgId) });
      const lists = queryClient.getQueriesData({ queryKey: qk.contacts(orgId) });

      for (const [key, value] of lists) {
        const page = value as ContactListResponse | undefined;
        if (!page || !Array.isArray(page.items)) continue;
        queryClient.setQueryData(key, {
          ...page,
          items: page.items.filter((row: ContactSummaryDto) => row.id !== contactId),
        });
      }
      return { lists: lists as [readonly unknown[], unknown][] };
    },
    onError: (_error, _contactId, context) => {
      for (const [key, value] of context?.lists ?? []) queryClient.setQueryData(key, value);
    },
    // Invitations too: an open one is withdrawn with the contact (QA5 A). Volunteers: sak 207.
    meta: {
      invalidates: [
        qk.contacts(orgId),
        qk.members(orgId),
        qk.invitations(orgId),
        qk.volunteers(orgId),
      ],
    },
  });
}

/**
 * Undo: `POST /v1/contacts/:id/restore` — the very row, by id (QA5 A).
 *
 * This used to re-send the snapshot to `POST /v1/contacts`, which found the deleted row by
 * ADDRESS. Two contacts sharing one address then swapped histories: undoing B woke A. The soft
 * delete leaves the row untouched, so nothing needs to travel back from the client.
 */
export function useRestoreContact(orgId: string): UseMutationResult<ContactDto, unknown, string> {
  return useMutation({
    mutationFn: (contactId: string) =>
      apiRequest({
        path: `/contacts/${contactId}/restore`,
        method: 'POST',
        schema: contactDtoSchema,
      }),
    meta: { invalidates: [qk.contacts(orgId), qk.members(orgId)] },
  });
}

export interface InviteContactInput {
  contactId: string;
  body: InviteContactRequest;
}

/**
 * Invite a contact to become a user. The person already exists, so accepting can never mint a
 * duplicate — which is the second half of the page's acceptance criterion.
 *
 * Decision 0007 lives in the backend: `invitation:create` carries the rank rule and answers
 * `cannot_grant_higher_role`. The dialog does not re-implement it; it asks the same `can()` so
 * an ungrantable role is never offered, and renders the server's refusal if one arrives anyway.
 */
export function useInviteContact(
  orgId: string,
): UseMutationResult<InvitationCreatedResponse, unknown, InviteContactInput> {
  return useMutation({
    mutationFn: ({ contactId, body }: InviteContactInput) =>
      apiRequest({
        path: `/contacts/${contactId}/invite`,
        method: 'POST',
        body,
        schema: invitationCreatedResponseSchema,
      }),
    meta: { invalidates: [qk.contacts(orgId), qk.members(orgId), qk.invitations(orgId)] },
  });
}
